import clsx from 'clsx';
import { useAnimate } from 'framer-motion';
import DOMPurify from 'isomorphic-dompurify';
import Image from 'next/image';
import { useState } from 'react';

import FiveStars from '@/components/FiveStars';
import type { YotpoReview } from '@/pages/api/yotpo';

interface LoginReviewsProps {
  reviews: YotpoReview[];
  className?: string;
}

const ANIMATION_DURATION = 0.5;

export const LoginReviews: React.FC<LoginReviewsProps> = ({ className, reviews }) => {
  const [reviewIndex, setReviewIndex] = useState(0);
  const [showMore, setShowMore] = useState(false);

  const [scope, animate] = useAnimate();

  const prevReview = () => {
    animate(scope.current, { opacity: 0, x: '100%' }, { duration: ANIMATION_DURATION }).then(() => {
      setReviewIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
      setShowMore(false);
      animate(scope.current, { opacity: 1, x: ['-100%', '0%'] }, { duration: ANIMATION_DURATION });
    });
  };

  const nextReview = () => {
    animate(scope.current, { opacity: 0, x: '-100%' }, { duration: ANIMATION_DURATION }).then(
      () => {
        setReviewIndex((prev) => (prev + 1) % reviews.length);
        setShowMore(false);
        animate(scope.current, { opacity: 1, x: ['100%', '0%'] }, { duration: ANIMATION_DURATION });
      }
    );
  };

  const toggleShowMore = () => setShowMore((prev) => !prev);

  return (
    <div className="relative flex flex-col items-center justify-center max-w-[1000px] px-5 py-14 bg-[url('/login/gradient.webp')] bg-cover overflow-hidden lg:basis-1/2 lg:p-10">
      <div
        ref={scope}
        className="flex flex-col flex-grow justify-start h-full max-w-[560px] mx-auto space-y-8 lg:justify-center"
      >
        <FiveStars className="justify-center" />
        <div>
          <span
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(reviews?.[reviewIndex]?.content ?? ''),
            }}
            className={clsx(
              'font-light text-xl lg:text-2xl lg:line-clamp-[11]',
              !showMore && 'line-clamp-6'
            )}
          />
          <button className="inline-block ml-2 lg:hidden" onClick={toggleShowMore}>
            show {showMore ? 'less' : 'more'}
          </button>
        </div>
        <div className="text-lg">{reviews?.length && reviews?.[reviewIndex]?.name}</div>
      </div>
      {/* Pagination buttons */}
      <div className="flex items-center gap-12 z-10 justify-self-end mt-6 lg:mt-auto">
        <button
          className="flex justify-center items-center h-12 w-12 rounded-full bg-alter-bone-50"
          type="button"
          onClick={prevReview}
        >
          <Image
            src={'/icons/arrow-small-right.svg'}
            alt="previous"
            height={24}
            width={24}
            className="rotate-180"
          />
        </button>
        <div className="flex gap-2">
          {reviews?.map((_, idx) => (
            <span
              key={idx}
              className={`h-2 w-2 rounded-full ${reviewIndex === idx ? 'bg-alter-black' : 'bg-alter-bone'}`}
            />
          ))}
        </div>
        <button
          className="flex justify-center items-center h-12 w-12 rounded-full bg-alter-bone-50"
          type="button"
          onClick={nextReview}
        >
          <Image src={'/icons/arrow-small-right.svg'} alt="next" height={24} width={24} />
        </button>
      </div>
    </div>
  );
};
